import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  CheckCircle, X, User, MapPin, Gauge, Mail, Phone, Clock, Sun, Home
} from 'lucide-react';

export const AdminRegistrationsTab = ({
  pendingRegistrations, handleApprove, handleReject, handleSendRecap, loading
}) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div>
        <h3 className="text-lg font-semibold">Demandes d'inscription en attente</h3>
        <p className="text-sm text-muted-foreground mt-0.5">
          {pendingRegistrations.length} demande{pendingRegistrations.length > 1 ? 's' : ''} en attente de validation
        </p>
      </div>
      <Button
        onClick={handleSendRecap}
        variant="outline"
        size="sm"
        className="text-xs"
        data-testid="send-daily-recap-btn"
      >
        <Mail className="w-3.5 h-3.5 mr-1.5" />
        Envoyer le récap quotidien
      </Button>
    </div>

    {pendingRegistrations.length === 0 ? (
      <Card className="border border-slate-100">
        <CardContent className="p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-green-50 border border-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <p className="text-muted-foreground font-medium">Aucune inscription en attente</p>
          <p className="text-sm text-muted-foreground mt-1">Toutes les demandes ont été traitées</p>
        </CardContent>
      </Card>
    ) : (
      <div className="grid gap-4">
        {pendingRegistrations.map((reg) => (
          <Card key={reg.id} className="border border-amber-100/80 bg-white" data-testid={`registration-card-${reg.id}`}>
            <CardContent className="p-5">
              <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                {/* User info */}
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center shrink-0">
                      <User className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{reg.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge className={reg.user_type === 'producer' ? 'bg-green-100 text-green-700 text-[10px]' : 'bg-blue-100 text-blue-700 text-[10px]'}>
                          {reg.user_type === 'producer' ? (
                            <><Sun className="w-3 h-3 mr-0.5" /> Producteur</>
                          ) : (
                            <><Home className="w-3 h-3 mr-0.5" /> Consommateur</>
                          )}
                        </Badge>
                        <Badge className="bg-amber-100 text-amber-700 text-[10px]">
                          <Clock className="w-3 h-3 mr-0.5" /> En attente
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{reg.email}</span>
                    </div>
                    {reg.phone && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="w-3.5 h-3.5 shrink-0" />
                        <span>{reg.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{reg.address || 'Non renseignée'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Gauge className="w-3.5 h-3.5 shrink-0" />
                      <span>PDL : {reg.pdl_linky || 'Non renseigné'}</span>
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Inscrit le {reg.created_at ? new Date(reg.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex lg:flex-col gap-2 shrink-0">
                  <Button
                    onClick={() => handleApprove(reg.id, reg.name)}
                    className="bg-green-600 hover:bg-green-700 text-white rounded-xl flex-1 lg:flex-none"
                    data-testid={`approve-registration-${reg.id}`}
                    disabled={loading}
                  >
                    <CheckCircle className="w-4 h-4 mr-1.5" /> Valider
                  </Button>
                  <Button
                    onClick={() => handleReject(reg.id, reg.name)}
                    variant="outline"
                    className="border-red-200 text-red-600 hover:bg-red-50 rounded-xl flex-1 lg:flex-none"
                    data-testid={`reject-registration-${reg.id}`}
                    disabled={loading}
                  >
                    <X className="w-4 h-4 mr-1.5" /> Refuser
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )}
  </div>
);
